import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  IndianRupee, 
  TrendingUp, 
  UserCheck,
  TrendingDown,
  ArrowRight,
  RefreshCw,
  CreditCard,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

/**
 * AdminDashboard - Premium Design
 * Matches the Owner module's dashboard styling exactly
 */
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planDistribution, setPlanDistribution] = useState([]);
  const [recentBusinesses, setRecentBusinesses] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch platform stats
      const { data: statsData } = await api.get('/admin/stats/overview');
      setStats(statsData);

      // Set plan distribution with colors
      const colorMap = {
        'BASIC': { bg: 'bg-slate-100', text: 'text-slate-700', bar: 'bg-slate-500' },
        'FIELD_TRACKING': { bg: 'bg-teal-100', text: 'text-teal-700', bar: 'bg-teal-500' },
        'STANDARD': { bg: 'bg-blue-100', text: 'text-blue-700', bar: 'bg-blue-500' },
        'ENTERPRISE': { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500' }
      };

      const planDist = statsData.plan_distribution?.map(plan => ({
        name: plan.name,
        code: plan.code,
        count: plan.count,
        ...colorMap[plan.code] || colorMap['BASIC']
      })) || [];
      setPlanDistribution(planDist);

      // Fetch recent businesses
      try {
        const { data: businessesData } = await api.get('/admin/recent/businesses', { params: { limit: 5 } });
        setRecentBusinesses(businessesData.businesses || []);
      } catch {
        // non-critical, ignore
      }

      setLoading(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load dashboard data'));
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="admin-dashboard">
        {/* Premium Header - Loading */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <h1 className="text-[18px] font-semibold text-slate-800">Platform Overview</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">Loading dashboard data...</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-light">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="admin-dashboard">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <h1 className="text-[18px] font-semibold text-slate-800">Platform Overview</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <AlertCircle className="w-12 h-12 text-rose-500" />
            <p className="text-[15px] font-medium text-slate-700">Error loading dashboard</p>
            <p className="text-[13px] text-slate-500">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!stats) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="admin-dashboard">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <h1 className="text-[18px] font-semibold text-slate-800">Platform Overview</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <Activity className="w-12 h-12 text-slate-300" />
            <p className="text-[15px] font-medium text-slate-600">No data available</p>
            <p className="text-[13px] text-slate-400">Platform statistics will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="admin-dashboard">
      {/* ═══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Same style as Owner Dashboard
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[18px] font-semibold text-slate-800">Platform Overview</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">Admin dashboard for platform management</p>
            </div>
            <button
              onClick={fetchDashboardData}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS PILLS - Same style as Owner Dashboard
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-[#F8F9FB] px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          {[
            { icon: Building2, label: 'Businesses', value: stats.total_businesses, color: 'slate' },
            { icon: UserCheck, label: 'Active', value: stats.active_businesses, color: 'emerald' },
            { icon: Users, label: 'Total Users', value: stats.total_users, color: 'blue' },
            { icon: IndianRupee, label: 'MRR', value: formatCurrency(stats.mrr || 0), color: 'purple' },
          ].map((stat, idx) => {
            const colorMap = {
              slate: { bg: 'bg-slate-50', icon: 'text-slate-500', value: 'text-slate-600' },
              emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-500', value: 'text-emerald-600' },
              blue: { bg: 'bg-blue-50', icon: 'text-blue-500', value: 'text-blue-600' },
              purple: { bg: 'bg-purple-50', icon: 'text-purple-500', value: 'text-purple-600' },
            };
            const colors = colorMap[stat.color];
            
            return (
              <div key={idx} className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-4 h-4 ${colors.icon}`} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{stat.label}</p>
                  <p className={`text-[16px] font-bold ${colors.value}`}>{stat.value}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-auto px-6 pb-5 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto space-y-6">
          
          {/* KPI Cards Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Businesses', value: stats.total_businesses, icon: Building2, color: 'slate' },
              { label: 'Active Businesses', value: stats.active_businesses, icon: CheckCircle, color: 'emerald' },
              { label: 'New (30d)', value: stats.new_businesses_30d || 0, icon: TrendingUp, color: 'blue' },
              { label: 'Total Users', value: stats.total_users, icon: Users, color: 'purple' },
              { label: 'MRR', value: formatCurrency(stats.mrr || 0), icon: IndianRupee, color: 'teal' },
              { label: 'Churn Rate', value: `${(stats.churn_rate || 0).toFixed(1)}%`, icon: TrendingDown, color: 'rose' },
            ].map((kpi, idx) => {
              const colorClasses = {
                slate: 'bg-slate-50 border-slate-200/50 text-slate-600',
                emerald: 'bg-emerald-50 border-emerald-200/50 text-emerald-600',
                blue: 'bg-blue-50 border-blue-200/50 text-blue-600',
                purple: 'bg-purple-50 border-purple-200/50 text-purple-600',
                teal: 'bg-teal-50 border-teal-200/50 text-teal-600',
                rose: 'bg-rose-50 border-rose-200/50 text-rose-600',
              };
              const Icon = kpi.icon;
              return (
                <div key={idx} className="bg-white rounded-xl border border-slate-200/60 p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[kpi.color]}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-slate-800">{kpi.value}</div>
                  <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">{kpi.label}</div>
                </div>
              );
            })}
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Plan Distribution */}
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-slate-700">Businesses by Plan</h3>
                <CreditCard className="w-4 h-4 text-slate-400" />
              </div>
              <div className="p-5 space-y-4">
                {planDistribution.length > 0 ? (
                  planDistribution.map((plan, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-[12px] font-medium px-2 py-0.5 rounded ${plan.bg} ${plan.text}`}>
                          {plan.name}
                        </span>
                        <span className="text-[13px] font-semibold text-slate-700">{plan.count}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${plan.bar} transition-all duration-500`}
                          style={{ width: `${stats.total_businesses > 0 ? (plan.count / stats.total_businesses) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                    <p className="text-[13px] text-slate-500">No plan data available</p>
                    <p className="text-[11px] text-slate-400 mt-1">Businesses will appear here once subscribed</p>
                  </div>
                )}
              </div>
            </div>

            {/* Platform Growth */}
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-slate-700">Platform Growth</h3>
                <Activity className="w-4 h-4 text-slate-400" />
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div>
                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Active Rate</div>
                    <div className="text-xl font-bold text-emerald-600">
                      {stats.total_businesses > 0 ? ((stats.active_businesses / stats.total_businesses) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <TrendingUp className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <div>
                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Avg Users/Business</div>
                    <div className="text-xl font-bold text-blue-600">
                      {stats.avg_users_per_business?.toFixed(1) || 0}
                    </div>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl border border-purple-100">
                  <div>
                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Revenue/Business</div>
                    <div className="text-xl font-bold text-purple-600">
                      {formatCurrency(stats.active_businesses > 0 ? stats.mrr / stats.active_businesses : 0)}
                    </div>
                  </div>
                  <IndianRupee className="w-8 h-8 text-purple-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Recent Businesses Table */}
          <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[14px] font-semibold text-slate-700">Recent Businesses</h3>
              <Link 
                to="/admin/businesses" 
                className="text-[12px] text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
              >
                View All <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            
            {recentBusinesses.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">Business Name</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">Owner</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">Plan</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">Status</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recentBusinesses.map((business, index) => (
                      <tr key={business.id} className={`hover:bg-rose-50/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-slate-500" />
                            </div>
                            <span className="text-[13px] font-medium text-slate-700">{business.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-[13px] text-slate-600">{business.owner_name || 'N/A'}</td>
                        <td className="px-5 py-3">
                          {business.plan ? (
                            <span className={`px-2 py-1 rounded text-[11px] font-medium ${
                              business.plan.includes('Field') || business.plan.includes('Tracking')
                                ? 'bg-teal-100 text-teal-700' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {business.plan}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-400">No plan</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-1 rounded text-[11px] font-medium ${
                            business.status === 'active' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {business.status || 'unknown'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-[12px] text-slate-500">
                          {business.created_at ? new Date(business.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building2 className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-[13px] text-slate-500">No businesses registered yet</p>
              </div>
            )}
          </div>

          {/* Payment Integration Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-[13px] font-semibold text-amber-800">Payment Integration Pending</h4>
              <p className="text-[12px] text-amber-700 mt-0.5">
                Transaction history and revenue tracking will be available once payment gateway (Razorpay) is integrated.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
